import React, { useCallback, useEffect, useState } from 'react';
import { MenuProps, Dropdown } from '@ui-library/dropdown';
import { Button, ButtonProps } from '@ui-library/button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsis } from '@fortawesome/pro-regular-svg-icons';
import { SizeType } from '@ui-library/types';

export const ContextMenuButton = (props: ButtonProps) => (
  <Button
    size="middle"
    {...props}
    type="default"
    shape="circle"
    icon={<FontAwesomeIcon icon={faEllipsis} />}
  />
);

export const ContextMenu: React.FC<{ menu: React.ReactElement<MenuProps>; disabled?: boolean }> = ({
  menu,
  disabled = false,
}) => (
  <Dropdown placement="bottomRight" trigger={['click']} overlay={menu} disabled={disabled}>
    <ContextMenuButton onClick={(e) => e.stopPropagation()} aria-label="Context menu" />
  </Dropdown>
);

export interface ContextMenuV2Props {
  items: MenuProps['items'];
  size?: SizeType;
  disabled?: boolean;
  tooltip?: ButtonProps['tooltip'];
  scrollContainerRef?: React.RefObject<HTMLElement>;
  testId?: string;
  id?: string;
}

export const ContextMenuV2: React.FC<ContextMenuV2Props> = ({
  items = [],
  disabled = false,
  size = 'middle',
  tooltip,
  scrollContainerRef = { current: window },
  testId,
  id,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const processedItems = React.useMemo(
    () =>
      items.map((item) => {
        if (!item || !('onClick' in item)) {
          return item;
        }

        const originalOnClick = item.onClick!;
        return {
          ...item,
          'data-testid': item.key,
          onClick: (info: Parameters<NonNullable<MenuProps['onClick']>>[0]) => {
            info.domEvent.stopPropagation();
            info.domEvent.preventDefault();
            originalOnClick(info);
            setIsOpen(false);
          },
        };
      }),
    [items],
  );

  const onClickHandler = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
  }, []);

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
  }, []);

  // Auto-dismiss dropdown when scroll container scrolls
  useEffect(() => {
    if (!scrollContainerRef?.current || !isOpen) {
      return undefined;
    }

    const scrollContainer = scrollContainerRef.current;

    const handleScroll = () => {
      setIsOpen(false);
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, [scrollContainerRef, isOpen]);

  return (
    <Dropdown
      placement="bottomRight"
      trigger={['click']}
      menu={{ items: processedItems }}
      disabled={disabled}
      open={isOpen}
      onOpenChange={handleOpenChange}
      destroyPopupOnHide
    >
      <ContextMenuButton
        size={size}
        onClick={onClickHandler}
        tooltip={tooltip}
        aria-label="Context menu"
        data-testid={`${testId}-context-menu`}
        id={`${id}-context-menu`}
      />
    </Dropdown>
  );
};
